import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import colours from 'theme/atoms/colours';

import * as RechartsStyled from 'dashboard/styles/Recharts.style';
import BarShape from './BarShape';

const PLACEHOLDER_DATA = [
  { date: '1', a: 4, b: 6, c: 8, d: 3 },
  { date: '2', a: 7, b: 3, c: 5, d: 6 },
  { date: '3', a: 2, b: 8, c: 4, d: 5 },
  { date: '4', a: 5, b: 4, c: 7, d: 2 },
  { date: '5', a: 3, b: 7, c: 6, d: 4 },
  { date: '6', a: 6, b: 5, c: 3, d: 7 },
  { date: '7', a: 4, b: 6, c: 5, d: 3 },
];

const PLACEHOLDER_BARS = ['a', 'b', 'c', 'd'] as const;

interface EmptyStateProps {
  isDesktop: boolean;
}

export default function EmptyState({ isDesktop }: EmptyStateProps) {
  const { t } = useTranslation('pageDashboard');

  return (
    <RechartsStyled.ChartEmptyWrapper>
      <RechartsStyled.ChartBlurredBackground>
        <ResponsiveContainer width="100%" height={420}>
          <BarChart
            data={PLACEHOLDER_DATA}
            barCategoryGap="4px"
            margin={{ left: isDesktop ? 20 : 0 }}
            style={{
              fontFamily: 'Inter, sans-serif',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
            }}
          >
            <XAxis
              dataKey="date"
              tickLine={false}
              stroke={colours.neutral[200]}
              tick={{
                fill: colours.neutral[300],
                fontSize: 13,
                fontWeight: 650,
                dy: 10,
              }}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
              tick={{
                fill: colours.neutral[300],
                fontSize: 13,
                fontWeight: 650,
              }}
              width={isDesktop ? undefined : 30}
              label={
                isDesktop
                  ? {
                      value: t('urgency.numberOfSignals'),
                      angle: -90,
                      position: 'insideLeft',
                      style: {
                        textAnchor: 'middle',
                        fill: colours.neutral[300],
                        fontSize: 13,
                        fontWeight: 650,
                        fontFamily: 'Inter, sans-serif',
                      },
                    }
                  : undefined
              }
            />
            <CartesianGrid stroke={colours.neutral[200]} vertical={false} />
            {PLACEHOLDER_BARS.map((key) => (
              <Bar
                key={key}
                dataKey={key}
                stackId="a"
                fill={colours.neutral[100]}
                shape={<BarShape />}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </RechartsStyled.ChartBlurredBackground>

      <RechartsStyled.ChartEmptyOverlay>
        <RechartsStyled.ChartEmptyCard>
          <RechartsStyled.ChartEmptyTextContent>
            <RechartsStyled.ChartEmptyTitle>
              {t('urgency.noData')}
            </RechartsStyled.ChartEmptyTitle>
            <RechartsStyled.ChartEmptyDescription>
              {t('urgency.noDataDescription')}
            </RechartsStyled.ChartEmptyDescription>
          </RechartsStyled.ChartEmptyTextContent>
        </RechartsStyled.ChartEmptyCard>
      </RechartsStyled.ChartEmptyOverlay>
    </RechartsStyled.ChartEmptyWrapper>
  );
}
